import { Box, Theme, Typography, styled } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { ISinglePost } from "@smartrr/shared/entities/Instagram";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { checkIsSafari } from "@smartrr/shared/utils/userAgentParsing";
import { InstagramFill } from "akar-icons";
import truncate from 'lodash/truncate';
import { rgba } from "polished";
import React from "react";

const isSafari = checkIsSafari();

const MAX_CHARACTERS = 80;

export const StyledCard = styled(AquariusCard)`
  box-shadow:
    0px 6px 12px -6px rgba(24, 39, 75, 0.12),
    0px 8px 24px -4px rgba(24, 39, 75, 0.08) !important;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 315px;
  justify-content: center;
  padding: 12px !important;
  transition: 0.3s ease-in-out;
  width: 254px;

  &:hover {
    background-color: ${props => rgba(props.theme.modernPageBackground!, 0.7)};
  }

  & img,
  & video {
    max-height: 230px;
    max-width: 230px;
    object-fit: cover;
    border-radius: 6px;
  }

  /* Hide Play button + controls on iOS */
  video::-webkit-media-controls {
    display: none !important;
  }

  & [class*="social-post-caption"] {
    & p {
      max-height: 32px;
      overflow: hidden;
      width: inherit;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }

  & [class*="social-post-date"] {
    margin-top: 3px;
    opacity: 80%;
  }
`;

export const SinglePostCard = ({ media_url, timestamp, caption, permalink }: ISinglePost) => {
  const isVideo =
    /\.(mp4|mov)(\?|$)/i.test(media_url) || // Checks for video extensions used for IG's video content (that includes videos and reels)
    /\/video\//.test(media_url) || // Avoids matches like '/notavideo/'
    /\/reel\/[^/]+\/?$/.test(permalink); // Avoids matches like '/notareel/', and checks for consistency with IG's permarlink format for reels

  return (
    <StyledCard className={prefixClassName("social-post-card")} onClick={() => window.open(permalink, "_blank")}>
      {isVideo ? (
        <video
          src={media_url}
          className={prefixClassName("social-post-video")}
          data-testid={prefixClassName("social-post-video")}
          onMouseOver={e => (e.target as HTMLVideoElement).play()}
          onMouseOut={e => (e.target as HTMLVideoElement).pause()}
          autoPlay={isSafari} // Mobile Safari doesn't allow videos to autoplay but its oddly required for it to render properly
          loop
          muted
          playsInline
        />
      ) : (
        <img
          data-testid={prefixClassName("social-post-image")}
          src={media_url}
          alt={truncate(caption, { length: 15, separator: "" })}
        />
      )}
      <Box mt={"10px"}>
        <div className={prefixClassName("social-post-caption")}>
          <Typography variant="caption1" component="p">
            {truncate(caption, { length: MAX_CHARACTERS })}
          </Typography>
        </div>
        <div className={prefixClassName("social-post-date")}>
          <Typography variant="caption1" component="p">
            {typedLocalize("utils.dates.toViewDateLong", {
              date: new Date(timestamp),
            })}
          </Typography>
        </div>
      </Box>
    </StyledCard>
  );
};

interface ISeeMoreCardProps {
  username: string;
  theme: Theme;
}

export const SeeMoreCard = ({ username, theme }: ISeeMoreCardProps) => (
  <StyledCard
    className={`${prefixClassName("social-post-card")} ${prefixClassName("view-more")}`}
    onClick={() => window.open(`https://www.instagram.com/${username}`, "_blank")}
    sx={{ alignItems: "center" }}
  >
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      width="170px"
      border={`1px solid ${theme.modernLineDividers}`}
      borderRadius="12px"
      height="180px"
    >
      <InstagramFill size="33" color={theme.modernIconsColor} style={{ marginBottom: "10px" }} />
      <Typography variant="h4" sx={{ marginBottom: "10px", overflowWrap: "anywhere", textAlign: "center" }}>
        @{username}
      </Typography>
      <AquariusButton href={`https://www.instagram.com/${username}`} sx={{ width: "max-content", margin: "0px" }}>
        {typedLocalize("social.viewMore")}
      </AquariusButton>
    </Box>
  </StyledCard>
);
